import React from 'react';
import { FiCircle, FiMoon, FiSun } from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../i18n';

/**
 * Theme switch — **three** modes, cycled by every tap:
 *
 *   Light → Dark → Black (AMOLED) → Light
 *
 * `black` is the pure-black mode (`theme-black` class, see index.css).
 * Default: a compact icon button (shown in the navbar on **every** screen size,
 * phones included). `withLabel` renders a full-width row with the label and a
 * switch — used inside the account dropdown so phone users can find it easily.
 */
const NEXT = {
  light: { key: 'themeDark', icon: FiMoon },
  dark: { key: 'themeBlack', icon: FiCircle },
  black: { key: 'themeLight', icon: FiSun },
};

export default function ThemeToggle({ className = '', withLabel = false }) {
  const { theme, isDark, toggle } = useTheme();
  const { t } = useLanguage();
  const next = NEXT[theme] || NEXT.light;
  const Icon = next.icon;
  const label = t(next.key);

  if (withLabel) {
    return (
      <button
        type="button"
        data-sfx="toggle"
        onClick={toggle}
        title={`${t('themeTitle')} · ${label}`}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition ${className}`}
        aria-label={`${t('themeTitle')}: ${label}`}
      >
        <Icon className="w-4 h-4" fill={theme === 'black' ? 'currentColor' : 'none'} />
        {label}
        <span className={`ml-auto w-9 h-5 rounded-full relative transition ${isDark ? 'bg-[#0B5FDB]' : 'bg-gray-300 dark:bg-gray-600'}`}>
          <span
            className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${theme === 'light' ? 'left-0.5' : theme === 'dark' ? 'left-[9px]' : 'left-[18px]'}`}
          />
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      data-sfx="toggle"
      onClick={toggle}
      className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition ${className}`}
      title={`${t('themeTitle')} · ${label}`}
      aria-label={`${t('themeTitle')}: ${label}`}
    >
      <Icon className="w-5 h-5 sm:w-4 sm:h-4" fill={theme === 'black' ? 'currentColor' : 'none'} />
    </button>
  );
}
